import { 
  collection, 
  doc, 
  getDoc,
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { AlertService } from './alertService';
import secureApiService from './secureApiService';

// Service pour gérer les transactions et les achats
export class TransactionService {
  static BILL_PAYMENT_OPERATOR_LABELS = {
    ade: 'ADE',
    sonelgaz: 'Sonelgaz',
    algerie_telecom: 'Algerie Telecom'
  };

  static getBalanceMethodKey(method) {
    if (method === 'payment') return 'payment';
    if (method === 'cash') return 'cash';
    return 'credit';
  }

  static getBalanceDirection(transaction = {}) {
    return transaction.direction === 'debit' ? 'debit' : 'credit';
  }

  static getBalanceMethodLabel(method) {
    const normalizedMethod = this.getBalanceMethodKey(method);

    if (normalizedMethod === 'payment') return 'Paiement';
    if (normalizedMethod === 'cash') return 'Espèces';
    return 'Crédit';
  }

  static getBalanceMovementLabel(transaction = {}) {
    const methodLabel = this.getBalanceMethodLabel(transaction.method);

    if (this.getBalanceDirection(transaction) === 'debit') {
      return `Récupération ${methodLabel.toLowerCase()}`;
    }

    return methodLabel;
  }

  static getSignedTransactionAmount(transaction = {}) {
    const amount = this.resolveTransactionAmount(transaction);

    if (transaction.type === 'recharge') {
      return this.getBalanceDirection(transaction) === 'debit' ? -amount : amount;
    }

    if (
      transaction.type === 'purchase' ||
      transaction.type === 'topup_order' ||
      transaction.type === 'bill_payment'
    ) {
      return -amount;
    }

    if (transaction.type === 'topup_refund' || transaction.type === 'bill_payment_refund') {
      return amount;
    }

    return amount;
  }

  static isTopupTransaction(transaction = {}) {
    return transaction.type === 'topup_order' || transaction.type === 'topup_refund';
  }

  static isBillPaymentTransaction(transaction = {}) {
    return transaction.type === 'bill_payment' || transaction.type === 'bill_payment_refund';
  }

  static getTopupTransactionLabel(transaction = {}) {
    const serviceName = transaction.productName || 'Topup';
    const offerLabel = transaction.offerValue || transaction.offerName || '';

    if (transaction.type === 'topup_refund') {
      return `Remboursement Topup ${serviceName}${offerLabel ? ` - ${offerLabel}` : ''}`;
    }

    return `Commande Topup ${serviceName}${offerLabel ? ` - ${offerLabel}` : ''}`;
  }

  static getBillPaymentOperatorLabel(transaction = {}) {
    const explicitServiceLabel = String(transaction.serviceLabel || '').trim();
    if (explicitServiceLabel) {
      return explicitServiceLabel;
    }

    const explicitLabel = String(transaction.operatorLabel || '').trim();
    if (explicitLabel) {
      return explicitLabel;
    }

    const operatorKey = String(transaction.operator || '').trim().toLowerCase();
    return this.BILL_PAYMENT_OPERATOR_LABELS[operatorKey] || 'Facture';
  }

  static getBillPaymentTransactionLabel(transaction = {}) {
    const operatorLabel = this.getBillPaymentOperatorLabel(transaction);
    const billLabel = String(transaction.bill || '').trim();

    if (transaction.type === 'bill_payment_refund') {
      return `Remboursement facture ${operatorLabel}${billLabel ? ` - ${billLabel}` : ''}`;
    }

    return `Paiement facture ${operatorLabel}${billLabel ? ` - ${billLabel}` : ''}`;
  }

  static resolveTransactionAmount(data = {}) {
    const explicitAmount = Number(data.amount);
    if (Number.isFinite(explicitAmount) && explicitAmount > 0) {
      return explicitAmount;
    }

    const totalCost = Number(data.totalCost);
    if (Number.isFinite(totalCost) && totalCost > 0) {
      return totalCost;
    }

    const unitPrice = Number(data.unitPrice);
    const quantity = Number(data.quantity || 1);
    if (Number.isFinite(unitPrice) && unitPrice > 0) {
      return Math.round(unitPrice * Math.max(quantity, 1) * 100) / 100;
    }

    return 0;
  }

  static normalizePurchase(docSnapshot) {
    const data = docSnapshot.data() || {};

    return {
      id: docSnapshot.id,
      ...data,
      type: 'purchase',
      amount: this.resolveTransactionAmount(data),
      description: data.description || data.productName || data.offerName || 'Achat de code',
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
    };
  }

  static normalizeLegacyRecharge(docSnapshot) {
    const data = docSnapshot.data() || {};

    return {
      id: docSnapshot.id,
      ...data,
      type: 'recharge',
      amount: this.resolveTransactionAmount(data),
      description: data.note || data.description || 'Recharge de solde',
      status: data.status || 'completed',
      method: data.method || 'manual_admin',
      direction: data.direction || 'credit',
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
    };
  }

  static async getUserLegacyRecharges(userId, includeLegacy = false) {
    if (!userId) {
      return [];
    }

    if (!includeLegacy) {
      return [];
    }

    try {
      const legacyRechargesQuery = query(
        collection(db, 'recharges'),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(legacyRechargesQuery);
      return snapshot.docs.map(doc => this.normalizeLegacyRecharge(doc));
    } catch (error) {
      console.error('Erreur lors de la récupération des anciennes recharges:', error);
      return [];
    }
  }

  static isCreditLedgerEntry(entry = {}) {
    if (entry.metadata?.affectsCreditBalance === true) {
      return true;
    }

    if (entry.metadata?.movementType === 'credit') {
      return true;
    }

    return typeof entry.description === 'string' && entry.description.startsWith('Recharge de solde');
  }

  static getSignedLedgerAmount(entry = {}) {
    const amount = this.resolveTransactionAmount(entry);
    const direction = entry.metadata?.direction || (entry.type === 'debit' ? 'debit' : 'credit');
    return direction === 'debit' ? -amount : amount;
  }

  static hasStoredCreditBalance(user = {}) {
    return Object.prototype.hasOwnProperty.call(user || {}, 'creditBalance');
  }

  static resolveEffectiveCreditBalance(user = {}, derivedCredit = 0, options = {}) {
    const storedCredit = Number(user?.creditBalance);
    const safeStoredCredit = Number.isFinite(storedCredit) ? Math.max(0, storedCredit) : 0;
    const safeDerivedCredit = Number.isFinite(derivedCredit) ? Math.max(0, derivedCredit) : 0;
    const fallbackBalance = Number(user?.balance);
    const safeFallbackBalance = Number.isFinite(fallbackBalance) ? Math.max(0, fallbackBalance) : 0;
    const allowBalanceFallback = options.allowBalanceFallback === true;
    const hasStoredCredit = this.hasStoredCreditBalance(user);

    // Le champ users.creditBalance devient la source prioritaire dès qu'il existe
    // pour éviter qu'un ancien recalcul historique "ressuscite" un crédit déjà récupéré.
    if (safeStoredCredit > 0) {
      return safeStoredCredit;
    }

    if (safeDerivedCredit > 0) {
      return safeDerivedCredit;
    }

    if (!hasStoredCredit && allowBalanceFallback && safeFallbackBalance > 0) {
      return safeFallbackBalance;
    }

    return safeStoredCredit;
  }

  static async getDerivedUserCreditBalance(userId, options = {}) {
    if (!userId) {
      return 0;
    }

    try {
      const includeLegacy = options.includeLegacy === true;
      const transactions = await this.getUserTransactions(userId);
      const legacyRecharges = await this.getUserLegacyRecharges(userId, includeLegacy);
      const transactionTotal = transactions.reduce((sum, operation) => {
        if (operation.type !== 'recharge') {
          return sum;
        }

        if (this.getBalanceMethodKey(operation.method) !== 'credit') {
          return sum;
        }

        return sum + this.getSignedTransactionAmount(operation);
      }, 0);

      const legacyTotal = legacyRecharges.reduce((sum, recharge) => {
        if (this.getBalanceMethodKey(recharge.method) !== 'credit') {
          return sum;
        }

        return sum + this.getSignedTransactionAmount(recharge);
      }, 0);

      return this.resolveEffectiveCreditBalance({}, transactionTotal + legacyTotal);
    } catch (error) {
      console.error('Erreur lors du recalcul du crédit depuis l\'historique:', error);
      return 0;
    }
  }
  
  // Recharger le solde d'un utilisateur (manuel par admin)
  static async rechargeBalance(userId, amount, adminId, method = 'manual') {
    try {
      const batch = writeBatch(db);
      
      // Référence utilisateur
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('Utilisateur introuvable');
      }
      
      const currentBalance = userDoc.data().balance || 0;
      const newBalance = currentBalance + amount;
      
      // Mettre à jour le solde
      batch.update(userRef, {
        balance: newBalance,
        lastRecharge: serverTimestamp()
      });
      
      // Créer l'entrée de transaction
      const transactionRef = doc(collection(db, 'transactions'));
      batch.set(transactionRef, {
        userId,
        type: 'recharge',
        amount,
        method,
        adminId,
        balanceBefore: currentBalance,
        balanceAfter: newBalance,
        status: 'completed',
        createdAt: serverTimestamp()
      });
      
      // Créer l'entrée dans le ledger
      const ledgerRef = doc(collection(db, 'ledger'));
      batch.set(ledgerRef, {
        userId,
        type: 'credit',
        amount,
        description: `Recharge de solde - ${method}`,
        adminId,
        transactionId: transactionRef.id,
        createdAt: serverTimestamp()
      });
      
      await batch.commit();
      
      return {
        success: true,
        newBalance,
        transactionId: transactionRef.id
      };
    } catch (error) {
      console.error('Erreur lors de la recharge:', error);
      throw error;
    }
  }
  
  // Acheter des codes de jeu (version sécurisée)
  static async purchaseCodes(userId, productId, offerId, quantity) {
    if (!userId || !productId || !offerId) {
      throw new Error('Paramètres manquants');
    }
    
    if (!quantity || quantity <= 0 || quantity > 100) {
      throw new Error('Quantité invalide (1-100)');
    }
    
    try {
      return await secureApiService.purchaseCodes(productId, offerId, quantity);
    } catch (error) {
      console.error('Erreur lors de l\'achat:', error);
      
      // Créer une alerte pour l'erreur système
      try {
        // Calculer le montant estimé basé sur les paramètres disponibles
        const estimatedAmount = quantity * 100; // Montant estimé par défaut
        await AlertService.alertPaymentError(
          userId, 
          estimatedAmount, 
          error.message || 'Erreur lors de l\'achat'
        );
      } catch (alertError) {
        console.error('Erreur lors de la création de l\'alerte:', alertError);
      }
      
      throw new Error(error.message || 'Erreur lors de l\'achat');
    }
  }
  
  // Récupérer l'historique des transactions d'un utilisateur
  static async getUserTransactions(userId) {
    try {
      const transactionsQuery = query(
        collection(db, 'transactions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(transactionsQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des transactions:', error);
      throw error;
    }
  }
  
  // Récupérer l'historique des achats d'un utilisateur
  static async getUserPurchases(userId) {
    try {
      const purchasesQuery = query(
        collection(db, 'purchases'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(purchasesQuery);
      return snapshot.docs.map(doc => this.normalizePurchase(doc));
    } catch (error) {
      console.error('Erreur lors de la récupération des achats:', error);
      throw error;
    }
  }

  // Récupérer toutes les transactions d'un utilisateur visibles dans l'application
  static async getAllUserTransactions(userId, options = {}) {
    try {
      const includeLegacy = options.includeLegacy === true;
      // Récupérer les transactions normales
      const allTransactions = await this.getUserTransactions(userId);
      const legacyRecharges = await this.getUserLegacyRecharges(userId, includeLegacy);
      
      // Récupérer les achats
      const purchases = await this.getUserPurchases(userId);
      const filteredTransactions = allTransactions.filter((transaction) => (
        !['freefire', 'freefire_purchase', 'freefire_request'].includes(transaction.type)
      ));
      
      // Combiner toutes les transactions
      const combinedTransactions = [...filteredTransactions, ...legacyRecharges, ...purchases];
      
      // Trier par date de création (plus récent en premier)
      return combinedTransactions.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Erreur lors de la récupération de toutes les transactions:', error);
      throw error;
    }
  }

  // Récupérer toutes les transactions de tous les utilisateurs (pour admin)
  static async getAllTransactions() {
    try {
      const allTransactions = [];

      // Récupérer toutes les transactions normales
      const transactionsQuery = query(
        collection(db, 'transactions'),
        orderBy('createdAt', 'desc')
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      transactionsSnapshot.forEach(doc => {
        const data = doc.data();
        if (['freefire', 'freefire_purchase', 'freefire_request'].includes(data.type)) {
          return;
        }

        allTransactions.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt)
        });
      });

      // Récupérer toutes les recharges
      const rechargesQuery = query(
        collection(db, 'recharges'),
        orderBy('createdAt', 'desc')
      );
      const rechargesSnapshot = await getDocs(rechargesQuery);
      
      rechargesSnapshot.forEach(doc => {
        const data = doc.data();
        allTransactions.push({
          id: doc.id,
          userId: data.userId,
          type: 'recharge',
          amount: data.amount,
          productName: 'Recharge de solde',
          description: data.note || 'Recharge de solde',
          status: 'completed',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
          method: data.method,
          adminId: data.adminId
        });
      });

      // Récupérer tous les achats de codes
      const purchasesQuery = query(
        collection(db, 'purchases'),
        orderBy('createdAt', 'desc')
      );
      const purchasesSnapshot = await getDocs(purchasesQuery);
      const purchases = purchasesSnapshot.docs.map(doc => this.normalizePurchase(doc));

      // Combiner toutes les transactions visibles côté admin
      const combinedTransactions = [...allTransactions, ...purchases];

      // Trier toutes les transactions par date
      return combinedTransactions.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Erreur lors de la récupération de toutes les transactions:', error);
      throw error;
    }
  }
}
